/* eslint-disable react-refresh/only-export-components */
import { ReactNode, useEffect, useRef } from "react";
import { LucideIcon, TrendingUp, TrendingDown, Minus } from "lucide-react";
import { Link } from "react-router-dom";
import { cn } from "@/lib/utils";
import { Badge, type BadgeProps } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { useAppLayoutTitleContext } from "@/components/layout/AppLayoutTitleContext";
import { useIsMobile } from "@/hooks/use-mobile";
import { useClaimPageSubNavSlot } from "@/components/layout/pageSubNavSlot";
import { allValuesEmpty } from "@/lib/kpi-empty-state";

export const ACTION_LAYOUT_SPEC = {
  primaryAction: "right",
  secondaryActions: "left",
  destructiveActions: "alert-dialog",
} as const;

const TAILWIND_SM_BREAKPOINT = 640;

export interface HeaderSummaryStatChange {
  /** Percentage change (e.g. 12 for +12%, -5 for -5%) */
  value: number;
  /** Comparison label (e.g. "vs last year", "vs prior period") */
  label: string;
}

export interface HeaderSummaryStat {
  label: string;
  value: string | number;
  icon?: LucideIcon;
  variant?: PageHeaderSummaryVariant;
  contextLine?: string;
  /** Trend indicator — shows a pill with up/down arrow and percentage */
  change?: HeaderSummaryStatChange;
  href?: string;
  onRetry?: () => void;
  /**
   * PPL-E2 / X-E1: the value is still loading. Renders a skeleton in place of the
   * number so the stat strip never flashes a misleading "0" before the real
   * count resolves (which reads as an empty org). Reusable across every page that
   * feeds PageHeader summary stats.
   */
  loading?: boolean;
}

export interface BreadcrumbItem {
  label: string;
  href?: string;
  /** Router state for the link, e.g. a list's entry to put back (see `listReturnState`). */
  state?: unknown;
  onClick?: () => void;
  icon?: LucideIcon;
}

export interface ContextChip {
  label: string;
  variant?: BadgeProps["variant"];
  icon?: LucideIcon;
  className?: string;
}

export interface PageHeaderProps {
  title: string;
  /** Persistent shell identity, not the destination page's arrival target. */
  titleIsShell?: boolean;
  subtitle?: string;
  hideTitle?: boolean;
  contextLine?: string;
  summaryStats?: HeaderSummaryStat[];
  /**
   * Rendered in place of the summary-stat grid when every stat is empty/zero
   * (and none is an error placeholder). Opt-in: pages without this prop keep
   * showing the full grid even when all-zero. Use it to collapse a wall of
   * "0"s into a slim first-run nudge.
   */
  summaryStatsEmptyState?: ReactNode;
  summaryStatValueSize?: "default" | "compact" | "finance";
  metaPrimarySlot?: ReactNode;
  metaSecondarySlot?: ReactNode;
  breadcrumbs?: BreadcrumbItem[];
  contextChips?: ContextChip[];
  actions?: ReactNode;
  primaryAction?: ReactNode;
  secondaryActions?: ReactNode;
  overflowActions?: ReactNode;
  rightSlot?: ReactNode;
  className?: string;
  sticky?: boolean;
}

export type PageHeaderSummaryVariant = keyof typeof HEADER_SUMMARY_VARIANTS;

export const PAGE_HEADER_CLASSNAMES = {
  container:
    "space-y-4",
  headerRow:
    "flex min-w-0 flex-col gap-4 sm:flex-row sm:items-start sm:justify-between",
  titleBlock:
    "min-w-0 space-y-2",
  title: "line-clamp-2 text-page-title font-display font-semibold tracking-tight md:line-clamp-none",
  subtitle: "line-clamp-1 text-meta text-muted-foreground md:line-clamp-none",
  contextLine: "line-clamp-1 text-meta font-medium text-foreground/80 md:line-clamp-none",
  metaSlots: "flex flex-wrap items-center gap-2 text-meta text-muted-foreground",
  actionsContainer:
    "flex w-full min-w-0 flex-col gap-2 sm:w-auto sm:flex-row sm:items-center sm:justify-between",
  statContainer: "flex flex-wrap gap-3 pt-3",
  // min-w bumped 180 → 200 (UX refresh 2026-05) so longer labels like
  // "Acknowledgements" / "Upcoming absences" don't get squeezed when 5-6
  // cards share a row (HR Overview).
  statCard: "hero-stat flex min-w-[200px] flex-1 items-center gap-3",
  contextLineBase: "text-meta text-muted-foreground",
};

export const HEADER_SUMMARY_VARIANTS = {
  default: {
    container: "bg-white dark:bg-card border-border shadow-soft-md",
    value: "text-foreground",
    icon: "bg-primary/10 text-primary",
    text: "text-muted-foreground",
  },
  success: {
    container: "bg-success-muted/60 dark:bg-success-muted/35 border-success-200/70 dark:border-success-700/60 shadow-soft-md",
    value: "text-foreground",
    icon: "bg-success/10 text-success",
    text: "text-muted-foreground",
  },
  // Semantic scales reverse in dark mode: 50 is the tint, 700 the readable accent.
  warning: {
    container: "bg-white dark:bg-warning-50 border-warning-200/70 dark:border-warning-200 shadow-soft-md",
    value: "text-warning-700",
    icon: "bg-warning/10 text-warning dark:text-warning-700",
    text: "text-warning-700",
  },
  error: {
    container: "bg-white dark:bg-error-50 border-error-200/70 dark:border-error-200 shadow-soft-md",
    value: "text-error-700",
    icon: "bg-destructive/10 text-destructive dark:text-error-700",
    text: "text-error-700",
  },
} as const;

const statVariantStyles = {
  default: "text-muted-foreground",
  success: "text-muted-foreground",
  warning: "text-warning-700",
  error: "text-error-700",
} as const;

const TREND_PILL_COLORS = {
  positive: "bg-success/15 dark:bg-success-50 text-success-700 ring-1 ring-success/20 font-semibold",
  negative: "bg-destructive/15 dark:bg-error-50 text-error-700 ring-1 ring-destructive/20 font-semibold",
  neutral: "bg-muted text-muted-foreground ring-1 ring-border/40 font-semibold",
} as const;

export function Breadcrumbs({ items, className }: { items: BreadcrumbItem[]; className?: string }) {
  if (!items.length) return null;

  return (
    <nav className={cn("flex flex-wrap items-center gap-1 text-meta text-muted-foreground", className)}>
      {items.map((item, index) => {
        const Icon = item.icon;
        const content = (
          <span className="inline-flex items-center gap-1">
            {Icon && <Icon className="h-3.5 w-3.5" />}
            <span>{item.label}</span>
          </span>
        );

        return (
          <span key={`${item.label}-${index}`} className="inline-flex items-center gap-1">
            {item.href ? (
              <Link to={item.href} state={item.state} className="font-medium text-muted-foreground hover:text-foreground">
                {content}
              </Link>
            ) : item.onClick ? (
              <button
                type="button"
                onClick={item.onClick}
                className="font-medium text-muted-foreground hover:text-foreground"
              >
                {content}
              </button>
            ) : (
              <span className="font-medium text-muted-foreground">{content}</span>
            )}
            {index < items.length - 1 && <span className="text-muted-foreground/60">/</span>}
          </span>
        );
      })}
    </nav>
  );
}

export function ContextChips({ chips, className }: { chips: ContextChip[]; className?: string }) {
  if (!chips.length) return null;

  return (
    <div className={cn("flex flex-wrap items-center gap-2", className)}>
      {chips.map((chip, index) => {
        const Icon = chip.icon;
        return (
          <Badge
            key={`${chip.label}-${index}`}
            variant={chip.variant ?? "outline"}
            className={cn("gap-1 text-meta font-medium", chip.className)}
          >
            {Icon && <Icon className="h-3.5 w-3.5" />}
            {chip.label}
          </Badge>
        );
      })}
    </div>
  );
}

export function PageHeader({
  title,
  titleIsShell = false,
  subtitle,
  hideTitle,
  contextLine,
  summaryStats,
  summaryStatsEmptyState,
  summaryStatValueSize = "default",
  metaPrimarySlot,
  metaSecondarySlot,
  breadcrumbs,
  contextChips,
  actions,
  primaryAction,
  secondaryActions,
  overflowActions,
  rightSlot,
  className,
  sticky = false,
}: PageHeaderProps) {
  // A module host's phone section selector, when this header is the page's title (#6678).
  const subNavSlot = useClaimPageSubNavSlot();
  const shouldHideTitleFromLayout = useAppLayoutTitleContext();
  const isMobile = useIsMobile();
  const resolvedHideTitle = hideTitle ?? shouldHideTitleFromLayout;
  const primaryActionRef = useRef<HTMLDivElement | null>(null);
  const hasFocusedPrimaryActionRef = useRef(false);
  const hasStructuredActions = primaryAction || secondaryActions || overflowActions || rightSlot;
  const hasSummaryStats = summaryStats && summaryStats.length > 0;
  // Collapse the strip only when there's genuinely nothing to show — never when
  // a stat failed to load, so "couldn't load" can't be mistaken for "all clear".
  // `onRetry` is the load-error signal (an errored stat shows "—", which would
  // otherwise read as empty); `variant: "error"` alone is just styling for a
  // bad-but-real metric (e.g. Messaging's always-red "Needs Attention"), so it
  // must NOT block collapse.
  const summaryStatsAllEmpty =
    !!hasSummaryStats &&
    !!summaryStatsEmptyState &&
    !summaryStats.some((stat) => !!stat.onRetry) &&
    // PPL-E2 / X-E1: while any stat is still loading, don't collapse to the
    // empty-state nudge — the real counts haven't arrived yet.
    !summaryStats.some((stat) => !!stat.loading) &&
    allValuesEmpty(summaryStats.map((stat) => stat.value));
  const hasBreadcrumbs = breadcrumbs && breadcrumbs.length > 0;
  const hasContextChips = contextChips && contextChips.length > 0;
  const showTitleBlock = !resolvedHideTitle;
  const hasMetaSlots = metaPrimarySlot || metaSecondarySlot;
  const summaryStatValueClass =
    summaryStatValueSize === "compact"
      ? "text-xl font-semibold tracking-tight tabular-nums"
      : "text-h2 font-semibold tracking-tight tabular-nums";

  useEffect(() => {
    if (hasFocusedPrimaryActionRef.current) return;
    if (!primaryActionRef.current) return;
    const focusable = primaryActionRef.current.querySelector<HTMLElement>(
      "button, [href], input, select, textarea, [tabindex]:not([tabindex='-1'])",
    );
    if (!focusable) return;
    focusable.focus();
    hasFocusedPrimaryActionRef.current = true;
  }, [primaryAction]);

  const hasActionsRowContent = primaryAction || secondaryActions || overflowActions || rightSlot;

  const secondaryActionsContent = secondaryActions ? (
    <div className="order-2 flex w-full flex-col gap-2 sm:order-1 sm:mr-auto sm:w-auto sm:flex-row sm:flex-wrap sm:items-center sm:gap-2 [&_button]:w-full sm:[&_button]:w-auto [&_button.size-icon]:!w-auto [&_a]:w-full sm:[&_a]:w-auto [&_a.size-icon]:!w-auto">
      {secondaryActions}
    </div>
  ) : null;

  const primaryActionContent = primaryAction ? (
    <div
      ref={primaryActionRef}
      className="order-1 w-full [&_button]:w-full [&_button.size-icon]:!w-auto [&_a]:w-full [&_a.size-icon]:!w-auto sm:order-2 sm:w-auto sm:flex sm:items-center sm:justify-end sm:gap-2 sm:[&_button]:w-auto sm:[&_a]:w-auto"
    >
      {primaryAction}
    </div>
  ) : null;

  const actionsContent = hasStructuredActions && hasActionsRowContent ? (
    <div className="flex w-full min-w-0 flex-col gap-2 sm:w-auto sm:flex-row sm:items-center sm:gap-2">
      {secondaryActionsContent}
      {primaryActionContent}
      {(overflowActions || rightSlot) && (
        <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row sm:items-center sm:gap-2 [&_button]:w-full sm:[&_button]:w-auto [&_button.size-icon]:!w-auto [&_a]:w-full sm:[&_a]:w-auto [&_a.size-icon]:!w-auto">
          {overflowActions}
          {rightSlot}
        </div>
      )}
    </div>
  ) : actions ? (
    <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row sm:items-center [&_button]:w-full sm:[&_button]:w-auto [&_button.size-icon]:!w-auto [&_a]:w-full sm:[&_a]:w-auto [&_a.size-icon]:!w-auto">
      {actions}
    </div>
  ) : null;

  return (
    <>
    <div className={cn(PAGE_HEADER_CLASSNAMES.container, className)}>
      <div
        className={cn(
          PAGE_HEADER_CLASSNAMES.headerRow,
          resolvedHideTitle && "sm:items-center sm:justify-end",
          sticky &&
            "sticky top-0 z-30 rounded-lg border border-border/70 bg-background px-4 py-3 shadow-sm",
        )}
      >
        {showTitleBlock && (
          <div className={PAGE_HEADER_CLASSNAMES.titleBlock}>
            {hasBreadcrumbs && <Breadcrumbs items={breadcrumbs ?? []} />}
            <h1 data-route-shell-heading={titleIsShell ? "" : undefined} className={cn(PAGE_HEADER_CLASSNAMES.title, "text-foreground")} title={title}>
              {title}
            </h1>
            {subtitle && <p className={PAGE_HEADER_CLASSNAMES.subtitle} title={subtitle}>{subtitle}</p>}
            {contextLine && <p className={PAGE_HEADER_CLASSNAMES.contextLine} title={contextLine}>{contextLine}</p>}
            {hasMetaSlots && (
              <div className={PAGE_HEADER_CLASSNAMES.metaSlots}>
                {metaPrimarySlot}
                {metaSecondarySlot}
              </div>
            )}
            {hasContextChips && <ContextChips chips={contextChips ?? []} />}
          </div>
        )}
        {actionsContent}
      </div>

      {hasSummaryStats && summaryStatsAllEmpty && (
        <div className={PAGE_HEADER_CLASSNAMES.statContainer}>{summaryStatsEmptyState}</div>
      )}

      {hasSummaryStats && !summaryStatsAllEmpty && (
        <div className={PAGE_HEADER_CLASSNAMES.statContainer}>
          {summaryStats.map((stat, index) => {
            const Icon = stat.icon;
            const variant = (stat.variant && stat.variant in HEADER_SUMMARY_VARIANTS) ? stat.variant : "default";
            const styles = HEADER_SUMMARY_VARIANTS[variant] ?? HEADER_SUMMARY_VARIANTS.default;
            const cardClassName = cn(
              PAGE_HEADER_CLASSNAMES.statCard,
              styles?.container,
              stat.href &&
                "cursor-pointer transition-colors hover:border-foreground/30 hover:bg-white/95 dark:hover:bg-card/95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
            );

            const content = (
              <>
                {Icon && (
                  <div
                    className={cn(
                      "flex h-9 w-9 shrink-0 items-center justify-center rounded-lg",
                      styles?.icon,
                    )}
                  >
                    <Icon className="h-[18px] w-[18px]" />
                  </div>
                )}
                <div className="min-w-0 flex-1">
                  <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
                    {stat.label}
                  </p>
                  {stat.loading ? (
                    <Skeleton className="mt-1 h-7 w-12" role="status" aria-label={`Loading ${stat.label}`} />
                  ) : (
                    <p className={cn(summaryStatValueClass, styles?.value)}>{stat.value}</p>
                  )}
                  {/* PPL-E2 (Codex #2908): the context line is usually count-derived
                      (e.g. "114 members"), so showing it while the value is a
                      skeleton would still flash a misleading "0 members". Hide it
                      until the stat resolves. */}
                  {!stat.loading && stat.contextLine && (
                    <p className={cn(PAGE_HEADER_CLASSNAMES.contextLineBase, statVariantStyles[variant])}>
                      {stat.contextLine}
                    </p>
                  )}
                  {!stat.loading && stat.change && (
                    <span
                      className={cn(
                        "mt-1 inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium",
                        stat.change.value > 0
                          ? TREND_PILL_COLORS.positive
                          : stat.change.value < 0
                            ? TREND_PILL_COLORS.negative
                            : TREND_PILL_COLORS.neutral,
                      )}
                    >
                      {stat.change.value > 0 ? (
                        <TrendingUp className="h-3 w-3" aria-hidden="true" />
                      ) : stat.change.value < 0 ? (
                        <TrendingDown className="h-3 w-3" aria-hidden="true" />
                      ) : (
                        <Minus className="h-3 w-3" aria-hidden="true" />
                      )}
                      {stat.change.value > 0 ? "+" : ""}
                      {stat.change.value}% {stat.change.label}
                    </span>
                  )}
                  {stat.onRetry && (
                    <button
                      type="button"
                      aria-label={`Retry ${stat.label}`}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        stat.onRetry?.();
                      }}
                      className="mt-1 text-meta font-medium text-error-700 underline underline-offset-2 hover:text-error-700 dark:text-foreground dark:hover:text-foreground"
                    >
                      Retry
                    </button>
                  )}
                </div>
              </>
            );

            if (stat.href) {
              return (
                <Link
                  key={`${stat.label}-${index}`}
                  to={stat.href}
                  className={cardClassName}
                  // PPL-E2 (Codex #2908): don't announce the stale value to screen
                  // readers while the skeleton is showing.
                  aria-label={stat.loading ? `${stat.label}: loading` : `${stat.label}: ${stat.value}`}
                >
                  {content}
                </Link>
              );
            }

            return (
              <div key={`${stat.label}-${index}`} className={cardClassName}>
                {content}
              </div>
            );
          })}
        </div>
      )}
    </div>
    {subNavSlot}
    </>
  );
}
